import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconGripVertical, IconEye, IconEyeOff, IconPin, IconPinFilled } from '@tabler/icons-react';
import { db, functions } from '../firebase';
import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getLogoForStyle } from '../utils/styleLogos';
import { useAdminView } from '../contexts/AdminViewContext';
import './AdminSettingsPage.css';

const DEFAULT_STRAVA_SHARE_FLOW_MODE = 'admin_media_upload';
const DEFAULT_3D_RENDER_MODE = 'browser_single';
const STRAVA_SHARE_FLOW_OPTIONS = [
  {
    value: 'manual_only',
    label: 'All shares use the manual flow',
    description: 'Everyone downloads the map and attaches it inside Strava manually.'
  },
  {
    value: 'admin_media_upload',
    label: 'Only admins use the media upload API',
    description: 'Admins upload directly to Strava. All other users stay on the manual flow.'
  },
  {
    value: 'all_media_upload',
    label: 'All users use the media upload API',
    description: 'Everyone uploads directly to Strava through the API when they tap the share button.'
  }
];

function normalizeStravaShareFlowMode(value) {
  if (STRAVA_SHARE_FLOW_OPTIONS.some((option) => option.value === value)) {
    return value;
  }

  return DEFAULT_STRAVA_SHARE_FLOW_MODE;
}

function normalize3DRenderMode(value) {
  if (value === 'renderer') return 'renderer';
  if (value === 'browser_search') return 'browser_search';
  return DEFAULT_3D_RENDER_MODE;
}

function normalizeRouteLineWidth(value) {
  const numericValue = Math.round(Number(value));
  if (!Number.isFinite(numericValue)) {
    return 5;
  }

  return Math.max(1, Math.min(40, numericValue));
}

// Draggable style card for reordering with hide and pin toggles
function SortableStyleCard({
  id,
  name,
  logoUrl,
  isHidden,
  isAdminOnly,
  isPinned,
  pinIndex,
  canPin,
  onToggleHidden,
  onTogglePin,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const resolvedLogo = getLogoForStyle(name, logoUrl);
  const cardClass = `style-order-card${isHidden ? ' style-hidden' : ''}${isAdminOnly ? ' style-admin-only' : ''}${isPinned ? ' style-pinned' : ''}`;
  const pinTitle = isPinned
    ? 'Remove from curated sequence for new users'
    : (canPin ? 'Pin to curated sequence for new users' : 'Cannot pin this style');
  return (
    <div ref={setNodeRef} style={style} className={cardClass}>
      <div className="style-card-drag" {...attributes} {...listeners}>
        {resolvedLogo ? (
          <img src={resolvedLogo} alt={name} />
        ) : (
          <span style={{ fontSize: '20px' }}>🗺️</span>
        )}
        <span className="style-order-name">{name}</span>
        {isPinned && <span className="style-curated-badge">Curated #{pinIndex}</span>}
        {isAdminOnly && <span className="style-admin-badge">Admin</span>}
        <IconGripVertical size={16} className="style-order-grip" />
      </div>
      <button
        className={`style-pin-btn${isPinned ? ' style-pin-btn-active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin(name);
        }}
        disabled={!isPinned && !canPin}
        title={pinTitle}
      >
        {isPinned ? <IconPinFilled size={16} /> : <IconPin size={16} />}
      </button>
      <button
        className="style-visibility-btn"
        onClick={(e) => {
          e.stopPropagation();
          onToggleHidden(name);
        }}
        title={isHidden ? 'Show style' : 'Hide style'}
      >
        {isHidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
      </button>
    </div>
  );
}

function AdminSettingsPage({ user }) {
  const navigate = useNavigate();
  const { isAdmin } = useAdminView();

  const [routeLineWidth, setRouteLineWidth] = useState(5);
  const [routeLineWidth3d, setRouteLineWidth3d] = useState(5);
  const [threeDRenderMode, setThreeDRenderMode] = useState(DEFAULT_3D_RENDER_MODE);
  const [force2DMapGeneration, setForce2DMapGeneration] = useState(false);
  const [stravaUploadShowLogo, setStravaUploadShowLogo] = useState(false);
  const [routeLineColor, setRouteLineColor] = useState("#FC5200");
  const [routeLineOpacity, setRouteLineOpacity] = useState(0.75);
  const [monthlyLimit, setMonthlyLimit] = useState(10);
  const [waterThreshold, setWaterThreshold] = useState(5.0);
  const [monthlyResetEnabled, setMonthlyResetEnabled] = useState(false);
  const [emailRegistrationCredits, setEmailRegistrationCredits] = useState(5);
  const [badgesVisibleToAll, setBadgesVisibleToAll] = useState(false);
  const [stravaShareFlowMode, setStravaShareFlowMode] = useState(DEFAULT_STRAVA_SHARE_FLOW_MODE);
  const [useGemini3Flash, setUseGemini3Flash] = useState(true);
  const [mapPadding2dPortraitTB, setMapPadding2dPortraitTB] = useState(200);
  const [mapPadding2dPortraitLR, setMapPadding2dPortraitLR] = useState(200);
  const [mapPadding2dLandscapeTB, setMapPadding2dLandscapeTB] = useState(200);
  const [mapPadding2dLandscapeLR, setMapPadding2dLandscapeLR] = useState(200);
  const [mapPadding3dPortrait, setMapPadding3dPortrait] = useState(100);
  const [mapPadding3dLandscape, setMapPadding3dLandscape] = useState(100);

  // Map padding preview
  const [previewActivityId, setPreviewActivityId] = useState('17610541728');
  const [previewPolyline, setPreviewPolyline] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [isLoadingAdminSettings, setIsLoadingAdminSettings] = useState(false);
  const [isSavingAdminSettings, setIsSavingAdminSettings] = useState(false);
  
  // Watermark detection settings
  const [watermarkSettings, setWatermarkSettings] = useState({
    confidenceThreshold: 0.6,
    uniformityRatioThreshold: 0.75,
    colorTolerance: 20,
    resolutionMatchWeight: 0.5,
    watermarkConfidenceWeight: 0.5
  });
  const [isLoadingWatermarkSettings, setIsLoadingWatermarkSettings] = useState(false);
  const [isSavingWatermarkSettings, setIsSavingWatermarkSettings] = useState(false);

  // Monthly recap email (admin-locked rollout)
  const [recapMonth, setRecapMonth] = useState(() => {
    const now = new Date();
    const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`;
  });
  const [recapDryRun, setRecapDryRun] = useState(true);
  const [recapForce, setRecapForce] = useState(false);
  const [recapEmails, setRecapEmails] = useState('');
  const [isSendingRecap, setIsSendingRecap] = useState(false);
  const [recapResult, setRecapResult] = useState(null);

  // Style ordering and visibility state
  const [adminStyleGroups, setAdminStyleGroups] = useState([]);
  const [globalStyleOrder, setGlobalStyleOrder] = useState([]);
  const [hiddenStyles, setHiddenStyles] = useState([]);
  const [hasGlobalOrder, setHasGlobalOrder] = useState(false);
  const [isLoadingStyles, setIsLoadingStyles] = useState(false);
  const [isSavingStyleOrder, setIsSavingStyleOrder] = useState(false);

  const MAX_CURATED_SEQUENCE_LENGTH = 5;
  const [autoUploadCuratedSequence, setAutoUploadCuratedSequence] = useState([]);
  const [autoUploadCuratedMaxPrior, setAutoUploadCuratedMaxPrior] = useState(3);
  const [isSavingCuratedMaxPrior, setIsSavingCuratedMaxPrior] = useState(false);
  const [pinWarning, setPinWarning] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Compute page assignments for styles as they'd appear to regular users
  // Hidden and adminOnly styles are not shown to regular users, so skip them
  const STYLES_PER_PAGE = 9;
  const stylePageMap = useMemo(() => {
    const map = {};
    let visibleIdx = 0;
    adminStyleGroups.forEach(group => {
      const isUserVisible = !hiddenStyles.includes(group.name) && !group.adminOnly;
      if (isUserVisible) {
        map[group.name] = Math.floor(visibleIdx / STYLES_PER_PAGE) + 1;
        visibleIdx++;
      }
    });
    return map;
  }, [adminStyleGroups, hiddenStyles]);

  // Predefined color options
  const colorOptions = [
    { name: "Strava Orange", value: "#FC5200" },
    { name: "Electric Blue", value: "#007BFF" },
    { name: "Forest Green", value: "#28A745" },
    { name: "Crimson Red", value: "#DC3545" },
    { name: "Purple", value: "#6F42C1" },
    { name: "Teal", value: "#20C997" },
    { name: "Gold", value: "#FFC107" },
    { name: "Dark Gray", value: "#495057" },
  ];

  useEffect(() => {
    if (user && isAdmin) {
      loadAdminSettings();
      loadWatermarkSettings();
      loadStyleTemplates();
      loadPreviewActivity(previewActivityId);
    }
  }, [user, isAdmin]);

  const loadAdminSettings = async () => {
    setIsLoadingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      const docSnap = await getDoc(settingsDocRef);
      if (docSnap.exists()) {
        const settingsData = docSnap.data();
        setRouteLineWidth(normalizeRouteLineWidth(settingsData?.routeLineWidth ?? 5));
        setRouteLineWidth3d(normalizeRouteLineWidth(settingsData?.routeLineWidth3d ?? 5));
        setThreeDRenderMode(normalize3DRenderMode(settingsData?.threeDRenderMode));
        setForce2DMapGeneration(settingsData?.force2DMapGeneration === true);
        setStravaUploadShowLogo(settingsData?.stravaUploadShowLogo === true);
        setRouteLineColor(settingsData?.routeLineColor || "#FC5200");
        setRouteLineOpacity(settingsData?.routeLineOpacity || 0.75);
        setMonthlyLimit(settingsData?.monthlyLimit || 10);
        setWaterThreshold(settingsData?.waterThreshold || 5.0);
        setMonthlyResetEnabled(settingsData?.monthlyResetEnabled || false);
        setEmailRegistrationCredits(settingsData?.emailRegistrationCredits || 5);
        setBadgesVisibleToAll(settingsData?.badgesVisibleToAll || false);
        setStravaShareFlowMode(normalizeStravaShareFlowMode(settingsData?.stravaShareFlowMode));
        // Default to true if not set (use Flash by default)
        setUseGemini3Flash(settingsData?.useGemini3Flash !== false);
        setMapPadding2dPortraitTB(settingsData?.mapPadding2dPortraitTB || 200);
        setMapPadding2dPortraitLR(settingsData?.mapPadding2dPortraitLR || 200);
        setMapPadding2dLandscapeTB(settingsData?.mapPadding2dLandscapeTB || 200);
        setMapPadding2dLandscapeLR(settingsData?.mapPadding2dLandscapeLR || 200);
        setMapPadding3dPortrait(settingsData?.mapPadding3dPortrait || 100);
        setMapPadding3dLandscape(settingsData?.mapPadding3dLandscape || 100);

        const rawSeq = Array.isArray(settingsData?.autoUploadFirstNTemplateIds)
          ? settingsData.autoUploadFirstNTemplateIds.filter(v => typeof v === 'string')
          : [];
        const seenBaseNames = new Set();
        const cleanedSeq = [];
        for (const entry of rawSeq) {
          let baseName = entry.trim();
          if (baseName.includes(' 3D')) baseName = baseName.replace(' 3D', '').trim();
          if (baseName.endsWith('3D')) baseName = baseName.slice(0, -2).trim();
          if (!baseName || seenBaseNames.has(baseName)) continue;
          seenBaseNames.add(baseName);
          cleanedSeq.push(baseName);
          if (cleanedSeq.length >= MAX_CURATED_SEQUENCE_LENGTH) break;
        }
        setAutoUploadCuratedSequence(cleanedSeq);

        const rawMaxPrior = settingsData?.autoUploadCuratedMaxPriorGenerations;
        setAutoUploadCuratedMaxPrior(
          typeof rawMaxPrior === 'number' && Number.isFinite(rawMaxPrior) && rawMaxPrior >= 0
            ? Math.floor(rawMaxPrior)
            : 3
        );
      }
    } catch (error) {
      console.error("Error loading admin settings:", error);
    } finally {
      setIsLoadingAdminSettings(false);
    }
  };

  const loadWatermarkSettings = async () => {
    setIsLoadingWatermarkSettings(true);
    try {
      const getWatermarkSettingsFunc = httpsCallable(functions, 'getWatermarkSettings');
      const result = await getWatermarkSettingsFunc();

      if (result.data?.settings) {
        setWatermarkSettings(result.data.settings);
      }
    } catch (error) {
      console.error("Error loading watermark settings:", error);
    } finally {
      setIsLoadingWatermarkSettings(false);
    }
  };

  const updateWatermarkSettings = async (settings) => {
    if (!user || !isAdmin) return;

    setIsSavingWatermarkSettings(true);
    try {
      const updateWatermarkSettingsFunc = httpsCallable(functions, 'updateWatermarkSettings');
      await updateWatermarkSettingsFunc({ settings });

      setWatermarkSettings(settings);
      alert("Watermark detection settings updated successfully!");
    } catch (error) {
      console.error("Error updating watermark settings:", error);
      alert("Failed to update watermark settings. Please try again.");
    } finally {
      setIsSavingWatermarkSettings(false);
    }
  };

  const updateRouteLineWidth = async (newWidth) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const normalizedWidth = normalizeRouteLineWidth(newWidth);
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        routeLineWidth: normalizedWidth
      }, { merge: true });
      setRouteLineWidth(normalizedWidth);
    } catch (error) {
      console.error("Error updating route line width:", error);
      alert("Failed to update route line width setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateRouteLineWidth3d = async (newWidth) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const normalizedWidth = normalizeRouteLineWidth(newWidth);
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        routeLineWidth3d: normalizedWidth
      }, { merge: true });
      setRouteLineWidth3d(normalizedWidth);
    } catch (error) {
      console.error("Error updating 3D route line width:", error);
      alert("Failed to update 3D route line width setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const update3DRenderMode = async (nextMode) => {
    if (!user || !isAdmin) return;

    const normalizedMode = normalize3DRenderMode(nextMode);
    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        threeDRenderMode: normalizedMode
      }, { merge: true });
      setThreeDRenderMode(normalizedMode);
    } catch (error) {
      console.error("Error updating 3D render mode:", error);
      alert("Failed to update 3D render mode setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateForce2DMapGeneration = async (nextEnabled) => {
    if (!user || !isAdmin) return;

    const enabled = nextEnabled === true;
    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        force2DMapGeneration: enabled
      }, { merge: true });
      setForce2DMapGeneration(enabled);
    } catch (error) {
      console.error("Error updating forced 2D map generation setting:", error);
      alert("Failed to update forced 2D map generation setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateStravaUploadShowLogo = async (nextEnabled) => {
    if (!user || !isAdmin) return;

    const enabled = nextEnabled === true;
    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        stravaUploadShowLogo: enabled
      }, { merge: true });
      setStravaUploadShowLogo(enabled);
    } catch (error) {
      console.error("Error updating Strava upload logo setting:", error);
      alert("Failed to update Strava upload logo setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateRouteLineColor = async (newColor) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        routeLineColor: newColor
      }, { merge: true });
      setRouteLineColor(newColor);
    } catch (error) {
      console.error("Error updating route line color:", error);
      alert("Failed to update route line color setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateRouteLineOpacity = async (newOpacity) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        routeLineOpacity: newOpacity
      }, { merge: true });
      setRouteLineOpacity(newOpacity);
    } catch (error) {
      console.error("Error updating route line opacity:", error);
      alert("Failed to update route line opacity setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateMonthlyLimit = async (newLimit) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        monthlyLimit: newLimit
      }, { merge: true });
      setMonthlyLimit(newLimit);
    } catch (error) {
      console.error("Error updating monthly limit:", error);
      alert("Failed to update monthly limit setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateWaterThreshold = async (newThreshold) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        waterThreshold: newThreshold
      }, { merge: true });
      setWaterThreshold(newThreshold);
    } catch (error) {
      console.error("Error updating water threshold:", error);
      alert("Failed to update water threshold setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateMonthlyResetEnabled = async (enabled) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const updateMonthlyResetEnabledFunc = httpsCallable(functions, 'updateMonthlyResetEnabled');
      await updateMonthlyResetEnabledFunc({ enabled });
      setMonthlyResetEnabled(enabled);
    } catch (error) {
      console.error("Error updating monthly reset enabled:", error);
      alert("Failed to update monthly reset setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const sendMonthlyRecap = async () => {
    if (!user || !isAdmin) return;

    setIsSendingRecap(true);
    setRecapResult(null);
    try {
      const emails = recapEmails
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean);
      const sendMonthlyRecapFunc = httpsCallable(functions, 'sendMonthlyRecapEmails');
      const response = await sendMonthlyRecapFunc({
        month: recapMonth,
        dryRun: recapDryRun,
        force: recapForce,
        ...(emails.length > 0 ? { emails } : {}),
      });
      setRecapResult(response.data);
    } catch (error) {
      console.error("Error sending monthly recap emails:", error);
      alert(`Failed to send monthly recap emails: ${error.message}`);
    } finally {
      setIsSendingRecap(false);
    }
  };

  const updateEmailRegistrationCredits = async (newCredits) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        emailRegistrationCredits: newCredits
      }, { merge: true });
      setEmailRegistrationCredits(newCredits);
      alert("Email registration credits updated successfully!");
    } catch (error) {
      console.error("Error updating email registration credits:", error);
      alert("Failed to update email registration credits. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateBadgesVisibleToAll = async (visible) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        badgesVisibleToAll: visible
      }, { merge: true });
      setBadgesVisibleToAll(visible);
      alert("Badge visibility setting updated successfully!");
    } catch (error) {
      console.error("Error updating badge visibility:", error);
      alert("Failed to update badge visibility setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateStravaShareFlowMode = async (newMode) => {
    if (!user || !isAdmin) return;

    const normalizedMode = normalizeStravaShareFlowMode(newMode);
    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        stravaShareFlowMode: normalizedMode
      }, { merge: true });
      setStravaShareFlowMode(normalizedMode);
    } catch (error) {
      console.error("Error updating Strava share flow mode:", error);
      alert("Failed to update Strava share flow mode. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateUseGemini3Flash = async (enabled) => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        useGemini3Flash: enabled
      }, { merge: true });
      setUseGemini3Flash(enabled);
    } catch (error) {
      console.error("Error updating Gemini 3 model setting:", error);
      alert("Failed to update Gemini 3 model setting. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const updateMapPaddings = async () => {
    if (!user || !isAdmin) return;

    setIsSavingAdminSettings(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        mapPadding2dPortraitTB,
        mapPadding2dPortraitLR,
        mapPadding2dLandscapeTB,
        mapPadding2dLandscapeLR,
        mapPadding3dPortrait,
        mapPadding3dLandscape,
      }, { merge: true });
    } catch (error) {
      console.error("Error updating map padding:", error);
      alert("Failed to update map padding settings. Please try again.");
    } finally {
      setIsSavingAdminSettings(false);
    }
  };

  const persistCuratedSequence = async (next) => {
    if (!user || !isAdmin) return;
    const validNames = new Set(
      adminStyleGroups
        .filter(g => !g.adminOnly)
        .map(g => g.name)
    );
    const seen = new Set();
    const cleaned = next
      .filter(name => typeof name === 'string' && validNames.has(name))
      .filter(name => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .slice(0, MAX_CURATED_SEQUENCE_LENGTH);

    setAutoUploadCuratedSequence(cleaned);

    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      if (cleaned.length > 0) {
        await setDoc(settingsDocRef, { autoUploadFirstNTemplateIds: cleaned }, { merge: true });
      } else {
        await setDoc(settingsDocRef, { autoUploadFirstNTemplateIds: deleteField() }, { merge: true });
      }
    } catch (error) {
      console.error('Error saving curated sequence:', error);
      alert('Failed to save curated sequence. Please try again.');
    }
  };

  const updateAutoUploadCuratedMaxPrior = async (next) => {
    if (!user || !isAdmin) return;
    const value = Math.max(0, Math.floor(Number(next) || 0));
    setIsSavingCuratedMaxPrior(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, { autoUploadCuratedMaxPriorGenerations: value }, { merge: true });
      setAutoUploadCuratedMaxPrior(value);
    } catch (error) {
      console.error('Error saving curated max-prior threshold:', error);
      alert('Failed to save eligibility threshold. Please try again.');
    } finally {
      setIsSavingCuratedMaxPrior(false);
    }
  };

  const loadPreviewActivity = async (activityId) => {
    if (!activityId) return;
    setIsLoadingPreview(true);
    try {
      const getActivityByIdFunc = httpsCallable(functions, 'getStravaActivityById');
      const result = await getActivityByIdFunc({ activityId });
      if (result.data?.map?.summary_polyline) {
        setPreviewPolyline(result.data.map.summary_polyline);
      }
    } catch (error) {
      console.error('Error loading preview activity:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';
  const MAPBOX_STYLE = 'samuelcollin3333/cmcyoppzj00ds01rf0gjpbt16';

  const previewPortraitUrl = useMemo(() => {
    if (!previewPolyline) return null;
    const color = routeLineColor.replace('#', '');
    const pathString = `path-${routeLineWidth}+${color}-${routeLineOpacity}(${encodeURIComponent(previewPolyline)})`;
    return `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/static/${pathString}/auto/854x1280?padding=${mapPadding2dPortraitTB},${mapPadding2dPortraitLR},${mapPadding2dPortraitTB},${mapPadding2dPortraitLR}&attribution=false&logo=false&access_token=${MAPBOX_TOKEN}`;
  }, [previewPolyline, mapPadding2dPortraitTB, mapPadding2dPortraitLR, routeLineWidth, routeLineColor, routeLineOpacity]);

  const previewLandscapeUrl = useMemo(() => {
    if (!previewPolyline) return null;
    const color = routeLineColor.replace('#', '');
    const pathString = `path-${routeLineWidth}+${color}-${routeLineOpacity}(${encodeURIComponent(previewPolyline)})`;
    return `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/static/${pathString}/auto/1280x853?padding=${mapPadding2dLandscapeTB},${mapPadding2dLandscapeLR},${mapPadding2dLandscapeTB},${mapPadding2dLandscapeLR}&attribution=false&logo=false&access_token=${MAPBOX_TOKEN}`;
  }, [previewPolyline, mapPadding2dLandscapeTB, mapPadding2dLandscapeLR, routeLineWidth, routeLineColor, routeLineOpacity]);

  const loadStyleTemplates = async () => {
    setIsLoadingStyles(true);
    try {
      // Fetch saved global order first
      let savedOrder = [];
      try {
        const settingsDocRef = doc(db, 'settings', 'mapGeneration');
        const settingsSnap = await getDoc(settingsDocRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          if (Array.isArray(data?.globalStyleOrder) && data.globalStyleOrder.length > 0) {
            savedOrder = data.globalStyleOrder;
            setGlobalStyleOrder(savedOrder);
            setHasGlobalOrder(true);
          }
          if (Array.isArray(data?.hiddenStyles)) {
            setHiddenStyles(data.hiddenStyles);
          }
        }
      } catch (err) {
        console.error('Error loading global style order:', err);
      }

      const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
      const result = await getTemplatesFunc();

      if (result.data && Array.isArray(result.data.templates)) {
        const templates = result.data.templates;

        // Group templates by base style name (same logic as SettingsPage)
        const groups = {};
        templates.forEach(template => {
          let baseName = template.name;
          if (baseName.includes(' 3D')) baseName = baseName.replace(' 3D', '').trim();
          if (baseName.endsWith('3D')) baseName = baseName.slice(0, -2).trim();

          if (!groups[baseName]) {
            groups[baseName] = { name: baseName, templates: [], logoUrl: null, adminOnly: true };
          }
          groups[baseName].templates.push(template);
          // A group is adminOnly only if ALL its templates are adminOnly
          if (!template.adminOnly) {
            groups[baseName].adminOnly = false;
          }
          if (template.logoUrl) {
            if (!groups[baseName].logoUrl || template.mapInputType !== '3d') {
              groups[baseName].logoUrl = template.logoUrl;
            }
          }
        });

        let groupsArray = Object.values(groups);

        // Apply saved global order if it exists
        if (savedOrder.length > 0) {
          groupsArray.sort((a, b) => {
            const aIdx = savedOrder.indexOf(a.name);
            const bIdx = savedOrder.indexOf(b.name);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;
            return a.name.localeCompare(b.name);
          });
        } else {
          groupsArray.sort((a, b) => a.name.localeCompare(b.name));
        }

        setAdminStyleGroups(groupsArray);
      }
    } catch (error) {
      console.error('Error loading style templates:', error);
    } finally {
      setIsLoadingStyles(false);
    }
  };

  const handleAdminDragEnd = async (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const oldIndex = adminStyleGroups.findIndex(g => g.name === active.id);
    const newIndex = adminStyleGroups.findIndex(g => g.name === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(adminStyleGroups, oldIndex, newIndex);
    setAdminStyleGroups(reordered);

    const newOrder = reordered.map(g => g.name);
    setGlobalStyleOrder(newOrder);
    setHasGlobalOrder(true);

    // Save to Firestore
    setIsSavingStyleOrder(true);
    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        globalStyleOrder: newOrder
      }, { merge: true });
    } catch (error) {
      console.error('Error saving global style order:', error);
      alert('Failed to save style order. Please try again.');
    } finally {
      setIsSavingStyleOrder(false);
    }

  };

  const handleTogglePin = async (styleName) => {
    setPinWarning('');
    const alreadyPinned = autoUploadCuratedSequence.includes(styleName);
    let next;
    if (alreadyPinned) {
      next = autoUploadCuratedSequence.filter(n => n !== styleName);
    } else {
      if (autoUploadCuratedSequence.length >= MAX_CURATED_SEQUENCE_LENGTH) {
        setPinWarning(`You can pin at most ${MAX_CURATED_SEQUENCE_LENGTH} styles. Unpin one first.`);
        return;
      }
      if (hiddenStyles.includes(styleName)) return;
      const group = adminStyleGroups.find(g => g.name === styleName);
      if (!group || group.adminOnly) return;
      next = [...autoUploadCuratedSequence, styleName];
    }
    await persistCuratedSequence(next);
  };

  const handleAdminResetOrder = async () => {
    setHasGlobalOrder(false);
    setGlobalStyleOrder([]);
    setAdminStyleGroups(prev => {
      const sorted = [...prev];
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      return sorted;
    });

    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      await setDoc(settingsDocRef, {
        globalStyleOrder: deleteField()
      }, { merge: true });
    } catch (error) {
      console.error('Error resetting global style order:', error);
    }
  };

  const handleToggleHidden = async (styleName) => {
    const isCurrentlyHidden = hiddenStyles.includes(styleName);
    const newHiddenStyles = isCurrentlyHidden
      ? hiddenStyles.filter(s => s !== styleName)
      : [...hiddenStyles, styleName];

    setHiddenStyles(newHiddenStyles);

    // Hiding a pinned style auto-unpins it.
    if (!isCurrentlyHidden && autoUploadCuratedSequence.includes(styleName)) {
      const nextCurated = autoUploadCuratedSequence.filter(n => n !== styleName);
      await persistCuratedSequence(nextCurated);
    }

    try {
      const settingsDocRef = doc(db, 'settings', 'mapGeneration');
      if (newHiddenStyles.length > 0) {
        await setDoc(settingsDocRef, { hiddenStyles: newHiddenStyles }, { merge: true });
      } else {
        await setDoc(settingsDocRef, { hiddenStyles: deleteField() }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating hidden styles:', error);
      // Revert on failure
      setHiddenStyles(hiddenStyles);
    }
  };

  const selectedStravaShareFlowOption = STRAVA_SHARE_FLOW_OPTIONS.find(
    (option) => option.value === stravaShareFlowMode
  ) || STRAVA_SHARE_FLOW_OPTIONS[1];

  const handleBack = () => {
    navigate(-1);
  };

  if (!user || !isAdmin) {
    return (
      <div className="admin-settings-page">
        <div className="admin-not-authorized">
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <div className="admin-settings-container">
        <div className="admin-settings-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            Back
          </button>
          <h1>Admin Settings</h1>
          <p className="admin-settings-subtitle">Configure system-wide settings for map generation. These settings affect all users.</p>
        </div>
        <div className="admin-divider"></div>

        {/* Default Style Ordering & curated sequence for new users */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Default style ordering</h4>
              <p>Set the default order of styles on the Create page. Drag to reorder, click the eye icon to hide a style, or click the pin icon to add a style to the curated intro sequence shown to newly-eligible auto-upload users (max {MAX_CURATED_SEQUENCE_LENGTH}).</p>
            </div>
            <div className="setting-control">
              {hasGlobalOrder && (
                <button onClick={handleAdminResetOrder} className="reset-order-btn">
                  Reset to alphabetical
                </button>
              )}
            </div>
          </div>

          {isLoadingStyles ? (
            <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
          ) : adminStyleGroups.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAdminDragEnd}>
              <SortableContext items={adminStyleGroups.map(g => g.name)} strategy={rectSortingStrategy}>
                <div className="style-order-grid">
                  {(() => {
                    let lastPage = 0;
                    return adminStyleGroups.map(group => {
                      const page = stylePageMap[group.name];
                      let divider = null;
                      if (page && page !== lastPage) {
                        divider = (
                          <div key={`page-${page}`} className="style-page-divider">
                            <span className="style-page-label">Page {page}</span>
                          </div>
                        );
                        lastPage = page;
                      }
                      const pinIdx = autoUploadCuratedSequence.indexOf(group.name);
                      const isPinned = pinIdx !== -1;
                      const canPin =
                        !group.adminOnly &&
                        !hiddenStyles.includes(group.name) &&
                        autoUploadCuratedSequence.length < MAX_CURATED_SEQUENCE_LENGTH;
                      return (
                        <React.Fragment key={group.name}>
                          {divider}
                          <SortableStyleCard
                            id={group.name}
                            name={group.name}
                            logoUrl={group.logoUrl}
                            isHidden={hiddenStyles.includes(group.name)}
                            isAdminOnly={group.adminOnly}
                            isPinned={isPinned}
                            pinIndex={isPinned ? pinIdx + 1 : 0}
                            canPin={canPin}
                            onToggleHidden={handleToggleHidden}
                            onTogglePin={handleTogglePin}
                          />
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </SortableContext>
            </DndContext>
          ) : null}

          {hasGlobalOrder && (
            <div className="setting-note" style={{ marginTop: '16px' }}>
              Custom order is active. This order will be used as the default for all users who haven't set their own preference.
            </div>
          )}

          {/* Curated intro sequence summary + eligibility threshold */}
          <div className="curated-sequence-section">
            <div className="curated-sequence-summary">
              {autoUploadCuratedSequence.length > 0 ? (
                <>Curated intro for new users: <strong>{autoUploadCuratedSequence.join(' → ')}</strong></>
              ) : (
                <>No styles pinned. Curated intro is inactive; new users fall through to existing logic.</>
              )}
            </div>
            {pinWarning && (
              <div className="curated-sequence-warning">{pinWarning}</div>
            )}
            <div className="curated-sequence-threshold">
              <div className="curated-sequence-threshold-label">
                <strong>Eligibility threshold</strong>
                <span>Max prior generations a user can have to qualify on their first auto-upload. Set to 0 to limit to brand-new users only.</span>
              </div>
              <div className="numeric-input-group">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={autoUploadCuratedMaxPrior}
                  onChange={(e) => setAutoUploadCuratedMaxPrior(parseInt(e.target.value, 10) || 0)}
                  disabled={isSavingCuratedMaxPrior}
                  className="numeric-input"
                />
                <button
                  onClick={() => updateAutoUploadCuratedMaxPrior(autoUploadCuratedMaxPrior)}
                  disabled={isSavingCuratedMaxPrior}
                  className="save-button"
                >
                  {isSavingCuratedMaxPrior ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Force 2D map generation</h4>
              <p>Hide the 2D/3D selector on Create and route new generations through the 2D pipeline.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={force2DMapGeneration}
                    onChange={(e) => updateForce2DMapGeneration(e.target.checked)}
                    disabled={isSavingAdminSettings}
                  />
                  <span className="toggle-slider"></span>
                </label>
              )}
            </div>
          </div>
          <div className="setting-note">
            {force2DMapGeneration
              ? '3D generation is currently disabled for users and new requests are forced to 2D.'
              : '3D generation is currently available where a style has a 3D variant.'}
          </div>
        </div>

        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>3D render mode</h4>
              <p>Choose how 3D maps are generated when forced 2D generation is off.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <select
                    value={threeDRenderMode}
                    onChange={(e) => setThreeDRenderMode(normalize3DRenderMode(e.target.value))}
                    disabled={isSavingAdminSettings || force2DMapGeneration}
                    className="numeric-input"
                  >
                    <option value="browser_single">Browser single render (no search)</option>
                    <option value="browser_search">Browser searched render</option>
                    <option value="renderer">3D renderer backend</option>
                  </select>
                  <button
                    onClick={() => update3DRenderMode(threeDRenderMode)}
                    disabled={isSavingAdminSettings || force2DMapGeneration}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            {force2DMapGeneration
              ? 'Ignored while forced 2D generation is enabled.'
              : `Current mode: ${threeDRenderMode === 'renderer'
                ? '3D renderer backend'
                : threeDRenderMode === 'browser_search'
                  ? 'Browser searched render'
                  : 'Browser single render (no search)'}.`}
          </div>
        </div>

        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>2D route line width</h4>
              <p>Set the global route thickness used for generated 2D maps for all users.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={routeLineWidth}
                    onChange={(e) => setRouteLineWidth(normalizeRouteLineWidth(e.target.value))}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">px</span>
                  <button
                    onClick={() => updateRouteLineWidth(routeLineWidth)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {routeLineWidth}px. This setting is used by the 2D generator.
          </div>
        </div>

        {/* 3D Route Line Width */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>3D route line width</h4>
              <p>Set the global route thickness used for browser and backend 3D renders for all users.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="1"
                    max="40"
                    value={routeLineWidth3d}
                    onChange={(e) => setRouteLineWidth3d(normalizeRouteLineWidth(e.target.value))}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">px</span>
                  <button
                    onClick={() => updateRouteLineWidth3d(routeLineWidth3d)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {routeLineWidth3d}px. This setting is used by 3D rendering.
          </div>
        </div>

        {/* Route Line Color */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Route line color</h4>
              <p>Set the color of route lines in generated maps.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="color-input-group">
                  <select
                    value={routeLineColor}
                    onChange={(e) => setRouteLineColor(e.target.value)}
                    disabled={isSavingAdminSettings}
                    className="color-select"
                  >
                    {colorOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <div
                    className="color-preview"
                    style={{ backgroundColor: routeLineColor }}
                    title={`Preview: ${routeLineColor}`}
                  ></div>
                  <button
                    onClick={() => updateRouteLineColor(routeLineColor)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {routeLineColor}
          </div>
        </div>

        {/* Route Line Opacity */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Route line opacity</h4>
              <p>Set the opacity of route lines in generated maps.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={routeLineOpacity}
                    onChange={(e) => setRouteLineOpacity(parseFloat(e.target.value) || 0.75)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">opacity</span>
                  <button
                    onClick={() => updateRouteLineOpacity(routeLineOpacity)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {routeLineOpacity.toFixed(2)}
          </div>
        </div>

        {/* Map Padding */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Map padding</h4>
              <p>Set the padding around the route in generated map images (in pixels). Each combination of dimension and format can be configured independently.</p>
            </div>
          </div>

          {isLoadingAdminSettings ? (
            <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
          ) : (
            <>
              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>2D Portrait &mdash; Top/Bottom</h5>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="50"
                    max="400"
                    value={mapPadding2dPortraitTB}
                    onChange={(e) => setMapPadding2dPortraitTB(parseInt(e.target.value) || 200)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>2D Portrait &mdash; Left/Right</h5>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="50"
                    max="400"
                    value={mapPadding2dPortraitLR}
                    onChange={(e) => setMapPadding2dPortraitLR(parseInt(e.target.value) || 200)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>2D Landscape &mdash; Top/Bottom</h5>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="50"
                    max="400"
                    value={mapPadding2dLandscapeTB}
                    onChange={(e) => setMapPadding2dLandscapeTB(parseInt(e.target.value) || 200)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>2D Landscape &mdash; Left/Right</h5>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="50"
                    max="400"
                    value={mapPadding2dLandscapeLR}
                    onChange={(e) => setMapPadding2dLandscapeLR(parseInt(e.target.value) || 200)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>3D Portrait</h5>
                  <p>Backend renderer fallback (uniform).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="20"
                    max="300"
                    value={mapPadding3dPortrait}
                    onChange={(e) => setMapPadding3dPortrait(parseInt(e.target.value) || 100)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>3D Landscape</h5>
                  <p>Backend renderer fallback (uniform).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="20"
                    max="300"
                    value={mapPadding3dLandscape}
                    onChange={(e) => setMapPadding3dLandscape(parseInt(e.target.value) || 100)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              {/* Live Preview */}
              <div style={{ marginTop: '20px', borderTop: '1px solid #e5e0d8', paddingTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Preview</h5>
                  <input
                    type="text"
                    value={previewActivityId}
                    onChange={(e) => setPreviewActivityId(e.target.value)}
                    placeholder="Strava activity ID"
                    style={{ flex: 1, padding: '4px 8px', border: '1px solid #d5d0c8', borderRadius: '4px', fontSize: '13px' }}
                  />
                  <button
                    onClick={() => loadPreviewActivity(previewActivityId)}
                    disabled={isLoadingPreview}
                    className="save-button"
                    style={{ padding: '4px 12px', fontSize: '13px' }}
                  >
                    {isLoadingPreview ? 'Loading...' : 'Load'}
                  </button>
                </div>

                {previewPolyline ? (
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '12px', color: '#6b6b6b', margin: '0 0 6px' }}>Portrait (854x1280)</p>
                      <img
                        src={previewPortraitUrl}
                        alt="Portrait preview"
                        style={{ width: '142px', height: '213px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e0d8' }}
                      />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '12px', color: '#6b6b6b', margin: '0 0 6px' }}>Landscape (1280x853)</p>
                      <img
                        src={previewLandscapeUrl}
                        alt="Landscape preview"
                        style={{ width: '256px', height: '171px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e0d8' }}
                      />
                    </div>
                  </div>
                ) : isLoadingPreview ? (
                  <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
                ) : (
                  <p style={{ fontSize: '13px', color: '#999', textAlign: 'center' }}>Enter an activity ID and click Load to preview padding</p>
                )}
              </div>

              <div className="watermark-actions">
                <button
                  onClick={updateMapPaddings}
                  disabled={isSavingAdminSettings}
                  className="save-button primary"
                >
                  {isSavingAdminSettings ? 'Saving...' : 'Save Padding Settings'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Monthly Limit */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Monthly map generation limit</h4>
              <p>Set the maximum number of maps that can be generated in a month.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={monthlyLimit}
                    onChange={(e) => setMonthlyLimit(parseInt(e.target.value) || 10)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">maps</span>
                  <button
                    onClick={() => updateMonthlyLimit(monthlyLimit)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {monthlyLimit} maps
          </div>
        </div>

        {/* Water Threshold */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Water threshold</h4>
              <p>Set the water percentage threshold for coastal/land style filtering.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="0.1"
                    max="50"
                    step="0.1"
                    value={waterThreshold}
                    onChange={(e) => setWaterThreshold(parseFloat(e.target.value) || 5.0)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">%</span>
                  <button
                    onClick={() => updateWaterThreshold(waterThreshold)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {waterThreshold}%
          </div>
        </div>

        {/* Monthly Reset */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Monthly reset</h4>
              <p>Automatically reset generation counts on the 1st of each month.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={monthlyResetEnabled}
                      onChange={(e) => updateMonthlyResetEnabled(e.target.checked)}
                      disabled={isSavingAdminSettings}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span style={{
                    color: monthlyResetEnabled ? '#10b981' : '#6b6b6b',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    {monthlyResetEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            {monthlyResetEnabled ? 'Counts reset on the 1st of each month' : 'Counts accumulate indefinitely'}
          </div>
        </div>

        {/* Monthly Recap Email */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Monthly recap email</h4>
              <p>Send "Your Month in Maps" recap emails for a given month. A cron also runs on the 1st for the previous month.</p>
            </div>
            <div className="setting-control">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="month"
                  value={recapMonth}
                  onChange={(e) => setRecapMonth(e.target.value)}
                  disabled={isSendingRecap}
                  className="numeric-input"
                  style={{ width: '160px' }}
                />
                <input
                  type="text"
                  value={recapEmails}
                  onChange={(e) => setRecapEmails(e.target.value)}
                  disabled={isSendingRecap}
                  className="numeric-input"
                  style={{ width: '260px' }}
                  placeholder="Limit to emails (comma-separated)"
                  title="Optional: only send to these emails (must still be admin recipients). Leave empty for all."
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={recapDryRun}
                    onChange={(e) => setRecapDryRun(e.target.checked)}
                    disabled={isSendingRecap}
                  />
                  Dry run
                </label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}
                  title="Resend even if this month's recap was already sent to the user"
                >
                  <input
                    type="checkbox"
                    checked={recapForce}
                    onChange={(e) => setRecapForce(e.target.checked)}
                    disabled={isSendingRecap}
                  />
                  Force resend
                </label>
                <button
                  onClick={sendMonthlyRecap}
                  disabled={isSendingRecap || !recapMonth}
                  className="save-button"
                >
                  {isSendingRecap ? 'Sending...' : (recapDryRun ? 'Preview send' : 'Send emails')}
                </button>
              </div>
            </div>
          </div>
          <div className="setting-note">
            {recapResult ? (
              <>
                {recapResult.monthLabel}: {recapResult.dryRun ? 'would send' : 'sent'} {recapResult.sent} · skipped {recapResult.skipped} · errors {recapResult.errors}
                {Array.isArray(recapResult.results) && recapResult.results.length > 0 && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                    {recapResult.results.map((r) => (
                      <li key={r.userId}>
                        {r.email || r.userId}: {r.status}{typeof r.mapCount === 'number' ? ` (${r.mapCount} maps)` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              'Recipients are limited to admin users while the feature is in testing.'
            )}
          </div>
        </div>

        {/* Strava Share Upload Flow */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Strava share upload flow</h4>
              <p>Choose whether shares use the old manual flow or the direct Strava media upload API. This setting is enforced by the backend.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <button
                  onClick={() => updateStravaShareFlowMode(stravaShareFlowMode)}
                  disabled={isSavingAdminSettings}
                  className="save-button"
                >
                  {isSavingAdminSettings ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
          <div className="share-flow-options">
            {STRAVA_SHARE_FLOW_OPTIONS.map((option) => (
              <label key={option.value} className={`share-flow-option${stravaShareFlowMode === option.value ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="stravaShareFlowMode"
                  value={option.value}
                  checked={stravaShareFlowMode === option.value}
                  onChange={(e) => setStravaShareFlowMode(e.target.value)}
                  disabled={isSavingAdminSettings || isLoadingAdminSettings}
                />
                <div className="share-flow-option__content">
                  <span className="share-flow-option__title">{option.label}</span>
                  <span className="share-flow-option__description">{option.description}</span>
                </div>
              </label>
            ))}
          </div>
          <div className="setting-note">
            Current mode: {selectedStravaShareFlowOption.label}
          </div>
        </div>

        {/* Strava upload logo */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Logo on Strava uploads</h4>
              <p>Show the Mythic Maps logo on maps uploaded to Strava, for both auto-uploads and manual shares. When off, the no-logo variant is uploaded.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={stravaUploadShowLogo}
                    onChange={(e) => updateStravaUploadShowLogo(e.target.checked)}
                    disabled={isSavingAdminSettings}
                  />
                  <span className="toggle-slider"></span>
                </label>
              )}
            </div>
          </div>
          <div className="setting-note">
            {stravaUploadShowLogo
              ? 'Maps uploaded to Strava currently include the Mythic Maps logo.'
              : 'Maps uploaded to Strava currently use the no-logo variant.'}
          </div>
        </div>

        {/* Email Registration Credits */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Email registration credits</h4>
              <p>Set how many credits users get for verifying their email.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div className="numeric-input-group">
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={emailRegistrationCredits}
                    onChange={(e) => setEmailRegistrationCredits(parseInt(e.target.value) || 5)}
                    disabled={isSavingAdminSettings}
                    className="numeric-input"
                  />
                  <span className="input-unit">credits</span>
                  <button
                    onClick={() => updateEmailRegistrationCredits(emailRegistrationCredits)}
                    disabled={isSavingAdminSettings}
                    className="save-button"
                  >
                    {isSavingAdminSettings ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current value: {emailRegistrationCredits} credits
          </div>
        </div>

        {/* Badges Visibility */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Badges visibility</h4>
              <p>Control whether badges are visible to all users or admin only.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={badgesVisibleToAll}
                      onChange={(e) => updateBadgesVisibleToAll(e.target.checked)}
                      disabled={isSavingAdminSettings}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span style={{
                    color: badgesVisibleToAll ? '#10b981' : '#6b6b6b',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    {badgesVisibleToAll ? 'Visible to All' : 'Admin Only'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Current status: Badges are {badgesVisibleToAll ? 'visible to all users' : 'only visible to administrators'}.
          </div>
        </div>

        {/* Gemini 3 Model Toggle */}
        <div className="admin-setting-card">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Gemini 3 model</h4>
              <p>Switch between Gemini 3.1 Flash (fast) and Gemini 3 Pro (high quality) for all Gemini 3 generations.</p>
            </div>
            <div className="setting-control">
              {isLoadingAdminSettings ? (
                <div className="loading-spinner-small"></div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={useGemini3Flash}
                      onChange={(e) => updateUseGemini3Flash(e.target.checked)}
                      disabled={isSavingAdminSettings}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span style={{
                    color: useGemini3Flash ? '#10b981' : '#6b6b6b',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    {useGemini3Flash ? '3.1 Flash' : '3 Pro'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="setting-note">
            Active API model: {useGemini3Flash ? 'gemini-3.1-flash-image-preview' : 'gemini-3-pro-image-preview'}. This applies to all templates set to "Gemini 3 Pro" — templates explicitly set to "Gemini 3.1 Flash" always use Flash.
          </div>
        </div>

        {/* Watermark Detection Settings */}
        <div className="admin-setting-card watermark-settings">
          <div className="setting-header">
            <div className="setting-info">
              <h4>Watermark Detection Settings</h4>
              <p>Configure the parameters for watermark detection in shared images.</p>
            </div>
          </div>

          {isLoadingWatermarkSettings ? (
            <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
          ) : (
            <>
              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>Confidence Threshold</h5>
                  <p>Minimum confidence score required for watermark detection (0-1).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={watermarkSettings.confidenceThreshold}
                    onChange={(e) => setWatermarkSettings({ ...watermarkSettings, confidenceThreshold: parseFloat(e.target.value) || 0.6 })}
                    disabled={isSavingWatermarkSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>Uniformity Threshold</h5>
                  <p>Minimum uniformity ratio required (0-1).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={watermarkSettings.uniformityRatioThreshold}
                    onChange={(e) => setWatermarkSettings({ ...watermarkSettings, uniformityRatioThreshold: parseFloat(e.target.value) || 0.75 })}
                    disabled={isSavingWatermarkSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>Color Tolerance</h5>
                  <p>Maximum color difference allowed (0-50).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={watermarkSettings.colorTolerance}
                    onChange={(e) => setWatermarkSettings({ ...watermarkSettings, colorTolerance: parseInt(e.target.value) || 20 })}
                    disabled={isSavingWatermarkSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>Resolution Match Weight</h5>
                  <p>Weight given to resolution matching (0-1).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={watermarkSettings.resolutionMatchWeight}
                    onChange={(e) => setWatermarkSettings({ ...watermarkSettings, resolutionMatchWeight: parseFloat(e.target.value) || 0.5 })}
                    disabled={isSavingWatermarkSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-setting-item">
                <div className="setting-info-small">
                  <h5>Watermark Confidence Weight</h5>
                  <p>Weight given to watermark detection confidence (0-1).</p>
                </div>
                <div className="setting-control-small">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={watermarkSettings.watermarkConfidenceWeight}
                    onChange={(e) => setWatermarkSettings({ ...watermarkSettings, watermarkConfidenceWeight: parseFloat(e.target.value) || 0.5 })}
                    disabled={isSavingWatermarkSettings}
                    className="numeric-input-small"
                  />
                </div>
              </div>

              <div className="watermark-actions">
                <button
                  onClick={() => updateWatermarkSettings(watermarkSettings)}
                  disabled={isSavingWatermarkSettings}
                  className="save-button primary"
                >
                  {isSavingWatermarkSettings ? 'Saving...' : 'Save Watermark Settings'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
